import { useState, useEffect } from 'react'
import { Calendar, User, FileText, Building2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { documentsAPI } from '@/utils/api'

/**
 * Document metadata form for legal documents.
 * Handles template selection, recipient info, dates, and case references.
 */
export default function DocumentMetadata({ metadata, onChange, disabled = false }) {
  const [templates, setTemplates] = useState([])
  const [loadingTemplates, setLoadingTemplates] = useState(true)

  // Fetch available templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        // Use centralized API with auth token
        const data = await documentsAPI.getTemplates()
        if (data.status === 'success' && Array.isArray(data.data)) {
          setTemplates(data.data)
        }
      } catch (err) {
        console.error('Failed to fetch templates:', err)
      } finally {
        setLoadingTemplates(false)
      }
    }
    fetchTemplates()
  }, [])

  const handleChange = (field, value) => {
    onChange({
      ...metadata,
      [field]: value,
    })
  }

  // Format date for input (YYYY-MM-DD)
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return ''
    // Handle both ISO and German date formats
    if (dateStr.includes('-')) {
      return dateStr.split('T')[0] // ISO format
    }
    // Convert German DD.MM.YYYY to YYYY-MM-DD
    const parts = dateStr.split('.')
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`
    }
    return dateStr
  }

  // Format date for API (ISO or German format)
  const formatDateForApi = (dateStr) => {
    if (!dateStr) return null
    return dateStr // Keep as YYYY-MM-DD for API
  }

  return (
    <div className="border rounded-lg p-4 bg-slate-50 space-y-4">
      <h3 className="font-semibold text-slate-700 flex items-center gap-2">
        <FileText className="h-4 w-4" />
        Document Settings
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Template Selection */}
        <div className="space-y-2">
          <Label htmlFor="template" className="text-sm font-medium">
            Template
          </Label>
          <Select
            value={metadata.template?.toString() || ''}
            onValueChange={(value) => handleChange('template', value ? parseInt(value) : null)}
            disabled={disabled || loadingTemplates}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a template..." />
            </SelectTrigger>
            <SelectContent>
              {templates.map((template) => (
                <SelectItem key={template.id} value={template.id.toString()}>
                  {template.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Document Date */}
        <div className="space-y-2">
          <Label htmlFor="document_date" className="text-sm font-medium flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            Document Date
          </Label>
          <Input
            id="document_date"
            type="date"
            value={formatDateForInput(metadata.document_date)}
            onChange={(e) => handleChange('document_date', formatDateForApi(e.target.value))}
            disabled={disabled}
          />
        </div>

        {/* Case Reference */}
        <div className="space-y-2">
          <Label htmlFor="case_reference" className="text-sm font-medium">
            Case Reference (Az.)
          </Label>
          <Input
            id="case_reference"
            placeholder="e.g., 0042/25/MB"
            value={metadata.case_reference || ''}
            onChange={(e) => handleChange('case_reference', e.target.value)}
            disabled={disabled}
          />
        </div>
      </div>

      {/* Recipient Section */}
      <div className="border-t pt-4 mt-4">
        <h4 className="font-medium text-slate-600 flex items-center gap-2 mb-3">
          <Building2 className="h-4 w-4" />
          Recipient
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="recipient_name" className="text-sm font-medium">
              Name / Institution
            </Label>
            <Input
              id="recipient_name"
              placeholder="e.g., Amtsgericht Düsseldorf"
              value={metadata.recipient_name || ''}
              onChange={(e) => handleChange('recipient_name', e.target.value)}
              disabled={disabled}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="recipient_address" className="text-sm font-medium">
              Address
            </Label>
            <Textarea
              id="recipient_address"
              placeholder="Street&#10;ZIP City"
              value={metadata.recipient_address || ''}
              onChange={(e) => handleChange('recipient_address', e.target.value)}
              disabled={disabled}
              rows={3}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
